import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Sparkles, Users } from "lucide-react";
import { EmptySeat, InitialDot } from "@/components/game/InitialDot";
import { sfx } from "@/lib/sfx";

const RoundTable = ({ team, seats, capacity, dia }) => {
  const bySeat = Object.fromEntries(seats.map((s) => [s.seat, s]));
  const total = Math.max(capacity, ...seats.map((s) => s.seat + 1), 1);
  const seat = Math.min(15, 210 / total);
  return (
    <div className="relative shrink-0" style={{ width: dia, height: dia }} data-testid={`board-table-${team.id}`}>
      <div className="absolute inset-[24%] rounded-full border-[3px] grid place-items-center text-center anim-pop"
        style={{ borderColor: team.color, background: `radial-gradient(circle at 50% 35%, ${team.color}40, ${team.color}12 70%)`, boxShadow: `0 0 40px -6px ${team.color}99, inset 0 0 30px ${team.color}33` }}>
        <div className="px-[6%]">
          <p className="font-display font-bold leading-tight text-[min(2rem,2.8vh)]" style={{ color: team.color }}>{team.name}</p>
          <p className="text-[min(1.4rem,2vh)] text-slate-200 mt-[0.4vh]">{team.count} kişi</p>
        </div>
      </div>
      {Array.from({ length: total }, (_, i) => {
        const ang = (i / total) * 2 * Math.PI;
        const s = bySeat[i];
        return (
          <div key={i} className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center"
            style={{ left: `${50 + 43 * Math.sin(ang)}%`, top: `${50 - 43 * Math.cos(ang)}%`, width: `${seat}%`, height: `${seat}%` }}>
            {s ? <div className="h-full w-full anim-pop"><InitialDot color={team.color} initials={s.initials} mascot={s.mascot} size="fill" /></div> : <EmptySeat fill />}
            {s?.name && <span className="absolute top-[calc(100%+0.3vh)] text-[min(1.2rem,1.9vh)] font-semibold text-white whitespace-nowrap max-w-[13vh] truncate px-[0.6vh] py-[0.1vh] rounded-full bg-[#0B0F16]/85 border border-white/10 z-10" data-testid={`seat-name-${team.id}-${i}`}>{s.name}</span>}
          </div>
        );
      })}
    </div>
  );
};


const SOLO = { id: "solo", color: "#FACC15" };

const useJoined = (state) => {
  const all = (state.joined || []).map((s) => ({ ...s, team: state.teams.find((t) => t.id === s.team_id) || SOLO }));
  const prev = useRef(null);
  const [latest, setLatest] = useState(null);
  const keys = all.map((x) => x.key).join("|");
  useEffect(() => {
    const known = prev.current;
    prev.current = new Set(keys.split("|").filter(Boolean));
    if (!known) return undefined;
    const fresh = all.find((x) => !known.has(x.key));
    if (!fresh) return undefined;
    setLatest(fresh);
    if (state.settings.sound) sfx.join();
    const t = setTimeout(() => setLatest(null), 3500);
    return () => clearTimeout(t);
  }, [keys]); // eslint-disable-line react-hooks/exhaustive-deps
  return { all, latest };
};

const JoinedStrip = ({ all }) => (
  <div className="panel !rounded-[1.5rem] px-[1.2vw] py-[1.2vh] shrink-0" data-testid="board-joined-list">
    <p className="eyebrow !text-[1.6vh] mb-[0.8vh]">Katılanlar · {all.length}</p>
    {all.length === 0 ? <p className="text-[2vh] text-slate-400">Henüz kimse katılmadı. İlk sen ol!</p> : (
      <div className="flex flex-wrap gap-[0.8vh] max-h-[11vh] overflow-hidden">
        {all.map((x) => (
          <span key={x.key} className="anim-pop flex items-center gap-[0.6vh] rounded-full pl-[0.4vh] pr-[1vh] py-[0.4vh] border-2 bg-[#0B0F16]/70" style={{ borderColor: `${x.team.color}99` }} data-testid="board-joined-chip">
            <span className="h-[3.6vh] w-[3.6vh]"><InitialDot color={x.team.color} initials={x.initials} mascot={x.mascot} size="fill" /></span>
            <span className="text-[min(1.4rem,2.2vh)] font-semibold">{x.name || x.initials}</span>
          </span>
        ))}
      </div>
    )}
  </div>
);

const SoloCloud = ({ all }) => (
  <div className="flex-1 min-h-0 flex flex-wrap items-center justify-center content-center gap-[1.6vh] py-[2vh]" data-testid="board-solo-cloud">
    {all.length === 0 && <p className="text-[3vh] text-slate-400">Henüz kimse katılmadı. İlk sen ol!</p>}
    {all.map((x) => (
      <span key={x.key} className="anim-pop flex items-center gap-[1vh] rounded-full pl-[0.6vh] pr-[1.6vh] py-[0.6vh] border-2 border-white/15 bg-[#161C27]/90" data-testid="board-joined-chip">
        <InitialDot color="#FACC15" initials={x.initials} mascot={x.mascot} size="vh" />
        <span className="font-display text-[min(2rem,3vh)] font-bold">{x.name || x.initials}</span>
      </span>
    ))}
  </div>
);

export const LobbyBoard = ({ state }) => {
  const { all, latest } = useJoined(state);
  const url = `${window.location.origin}/katil/${state.code}`;
  const n = state.teams.length;
  const dia = n <= 3 ? "min(36vh, 19vw)" : "min(26vh, 17vw)";
  return (
    <div className="h-full flex gap-[3vw]" data-testid="board-lobby">
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-end gap-[2vw]">
          <h1 className="font-display text-[min(5.5rem,8.5vh)] font-bold leading-none anim-fade-up">{state.solo ? "Oyuna katıl!" : "Masalara oturun!"}</h1>
          {latest && (
            <span key={latest.key} className="anim-pop flex items-center gap-2 rounded-full px-[1.2vw] py-[0.8vh] font-display text-[min(2rem,3vh)] font-bold border-2 mb-[0.5vh]"
              style={{ borderColor: latest.team.color, color: latest.team.color, boxShadow: `0 0 26px ${latest.team.color}66` }} data-testid="board-latest-joiner">
              <Sparkles className="h-[3vh] w-[3vh]" />{latest.name || latest.initials} katıldı!
            </span>
          )}
        </div>
        <p className="text-[min(1.6rem,2.4vh)] text-slate-300 mt-[1.2vh]">{state.solo ? "Telefonundan katıl ve atomunun rengini seç. Bu oyunda herkes kendi puanı için yarışıyor." : "Telefonundan katıl, masanı seç ve atomunun rengini belirle."}</p>
        {state.solo ? <SoloCloud all={all} /> : <>
        <div className="flex-1 min-h-0 flex flex-wrap items-center justify-center content-center gap-x-[3vw] gap-y-[3vh]">
          {state.teams.map((t) => <RoundTable key={t.id} team={t} dia={dia} seats={state.seating?.[t.id] || []} capacity={state.settings.seats_per_team} />)}
        </div>
        <JoinedStrip all={all} />
        </>}
      </div>
      <aside className="w-[min(26vw,46vh)] shrink-0 panel !rounded-[2rem] flex flex-col items-center justify-center gap-[2vh] px-[1.5vw] py-[3vh]">
        {state.settings.closed_session ? (
          <div className="text-center space-y-[1.5vh]" data-testid="board-closed-session">
            <p className="font-display text-[min(3rem,5vh)] font-bold text-[#FACC15]">Kapalı oturum</p>
            <p className="text-[min(1.4rem,2.2vh)] text-slate-300">Bu oyun yalnızca tahtada oynanır; telefondan katılım kapalı.</p>
          </div>
        ) : <>
        <div className="rounded-[1.5rem] bg-white p-[1.4vh]">
          <QRCodeSVG value={url} size={512} style={{ width: "min(30vh, 18vw)", height: "min(30vh, 18vw)", display: "block" }} data-testid="board-qr" />
        </div>
        <p className="text-[min(1.5rem,2.2vh)] text-slate-300">Oyun kodu</p>
        <p className="font-display text-[min(7rem,11vh)] leading-none font-bold tracking-[0.06em] text-[#FACC15]" data-testid="board-game-code">{state.code}</p>
        <p className="text-[min(1.3rem,1.9vh)] text-slate-300 text-center break-all">{url.replace(/^https?:\/\//, "")}</p>
        <div className="flex items-center gap-3 mt-[1vh]">
          <Users className="h-[4vh] w-[4vh] text-slate-200" />
          <span className="font-display text-[min(3.5rem,5vh)] font-bold" data-testid="board-student-count">{state.student_count}</span>
        </div>
        {state.unassigned > 0 && !state.solo && <p className="text-[1.8vh] text-slate-400">{state.unassigned} öğrenci masa bekliyor</p>}
        </>}
      </aside>
    </div>
  );
};
